// Resource Gantt — 포커스된 리소스의 가동률(utilization) 차트 (간트 아래 분할 영역)
// ResourceUtilization 스타일: 버킷별 부하/여유/초과 스택막대 + 부하율(%) 라인.
// 데이터는 gantt 버킷에서 도출: total=capacity, used=Σ주문부하, avail=total-used, over=used-total.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { red } from "@mui/material/colors";
import ColorUtil from "@plannel/pages/utils/color-util";
import { AgStyleTooltipContent } from "@plannel/components/rechart/AgStyleTooltip";
import { bucketCap, bucketUsed } from "@plannel/pages/master-plan/resource-gantt/ganttScales";
import { isOverloaded } from "@plannel/pages/master-plan/resource-gantt/ganttConstants";
import { FS } from "@plannel/utils/font-util";

const UOM_DIV = { seconds: 1, minutes: 60, hour: 3600 }; // capacity는 초(seconds) 단위로 저장

const ResourceUtilChart = ({ resource, buckets = [], bucketLabels = {}, timeUom = "seconds" }) => {
  const { t } = useTranslation();
  const div = UOM_DIV[timeUom] || UOM_DIV.minutes;

  const data = useMemo(
    () =>
      buckets.map((b) => {
        const total = bucketCap(b);
        const used = bucketUsed(b);
        return {
          label: bucketLabels[b.key] ?? b.key,
          load: Math.min(used, total) / div,
          avail: Math.max(total - used, 0) / div,
          // 초과 판정은 캔버스·패널과 동일하게 허용오차(isOverloaded)를 적용한다 —
          // 부동소수 잔차 수준의 미세 초과를 위/아래에서 다르게 표시하지 않도록.
          over: isOverloaded(used, total) ? Math.max(used - total, 0) / div : 0,
          util: total > 0 ? (used / total) * 100 : used > 0 ? 100 : 0,
        };
      }),
    [buckets, bucketLabels, div]
  );

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography variant="caption" color="text.secondary" sx={{ px: 1, flexShrink: 0 }}>
        {t("utilization")} — {resource?.name ?? resource?.id}
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: FS.BODY }} interval="preserveStartEnd" />
            <YAxis yAxisId="left" tick={{ fontSize: FS.BODY }} width={48} />
            <YAxis
              yAxisId="right"
              orientation="right"
              width={44}
              tick={{ fontSize: FS.BODY }}
              domain={[0, (m) => Math.max(100, Math.ceil(m))]}
              unit="%"
            />
            <Tooltip formatter={(v, n) => [typeof v === "number" ? v.toFixed(2) : v, n]} content={<AgStyleTooltipContent />} />
            <Legend wrapperStyle={{ fontSize: FS.BODY }} />
            <Bar yAxisId="left" dataKey="load" stackId="c" fill={ColorUtil.colorSet(0)} name={t("load")} barSize={18} />
            <Bar yAxisId="left" dataKey="avail" stackId="c" fill={ColorUtil.colorSet(1)} name={t("remainCapacity")} barSize={18} />
            <Bar yAxisId="left" dataKey="over" stackId="c" fill={red[400]} name={t("overload")} barSize={18} />
            <Line yAxisId="right" type="monotone" dataKey="util" stroke={ColorUtil.colorSet(2)} strokeWidth={2} dot={false} name={t("utilization")} />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default ResourceUtilChart;
